import React from 'react'
import styled from 'styled-components'
import GridCard from 'employee-platform/shared/components/GridCard'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import Divider from 'core-system/Divider'
import CreditCard from 'core-system/Icons/Accounts/CreditCard'
import palette from 'core-system/Themes/palette'
import { BaseProgram } from 'redux/employeePlatform/employeePlatformTypes'
import moment from 'moment'

const ProgramInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const ProgramDescription = styled(Text)`
  color: ${palette.text.secondary};
`

const AgenciesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.125rem;
`

const AgencyItem = styled(FlexContainer)`
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0;
`

const AgencyIconContainer = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background-color: ${palette.grey.grey100};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${palette.text.secondary};
  overflow: hidden;
`

const AgencyIcon = styled.img`
  width: 1.25rem;
  height: 1.25rem;
  object-fit: contain;
`

const AgencyInfo = styled(FlexContainer)`
  flex-direction: column;
  gap: 0.0625rem;
  min-width: 0;
`

interface TransitPassProgramCardProps {
  program: BaseProgram
}

const formatBudgetCycleLabel = (budgetCycle?: string | null): string => {
  const cycle = (budgetCycle || 'MONTHLY').toLowerCase()
  return cycle.charAt(0).toUpperCase() + cycle.slice(1)
}

const TransitPassProgramCard = ({ program }: TransitPassProgramCardProps) => {
  const agencies = program.transitAgencies ?? []
  const isPending = program.status === 'PENDING'
  const title = `${formatBudgetCycleLabel(program.budgetCycle)} Transit Pass`

  return (
    <GridCard headerTitle={title} borderColor={palette.secondary.purple400}>
      <FlexContainer flexDirection='column' style={{ gap: '0.75rem' }}>
        {(isPending || program.description) && (
          <ProgramInfoSection>
            {isPending ? (
              <Text variant='action4' textColor={palette.text.secondary}>
                Starts on {moment(program.startDate).format('MMM D, YYYY')}
              </Text>
            ) : null}
            {program.description ? (
              <ProgramDescription variant='action4'>
                {program.description}
              </ProgramDescription>
            ) : null}
          </ProgramInfoSection>
        )}

        {agencies.length > 0 ? (
          <>
            <Divider margin='0.5rem 0' />
            <AgenciesSection aria-label='Transit Agency'>
              <Text variant='action4' textColor={palette.text.secondary}>
                Transit Agency
              </Text>
              {agencies.map((agency, idx) => (
                <React.Fragment key={agency.id}>
                  {idx > 0 ? <Divider margin='0.25rem 0' /> : null}
                  <AgencyItem>
                    <AgencyIconContainer>
                      {agency.iconUrl ? (
                        <AgencyIcon src={agency.iconUrl} alt={agency.name} />
                      ) : (
                        <CreditCard width={16} height={16} />
                      )}
                    </AgencyIconContainer>
                    <AgencyInfo>
                      <Text variant='action2' fontWeight={600}>
                        {agency.name}
                      </Text>
                      {agency.region ? (
                        <Text
                          variant='action4'
                          textColor={palette.text.secondary}
                        >
                          {agency.region}
                        </Text>
                      ) : null}
                    </AgencyInfo>
                  </AgencyItem>
                </React.Fragment>
              ))}
            </AgenciesSection>
          </>
        ) : null}
      </FlexContainer>
    </GridCard>
  )
}

export default TransitPassProgramCard
